import React, { useEffect, useState } from 'react';
import Image from 'next/future/image';
import axios from 'axios';
import Cookies from 'js-cookie';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import 'react-datepicker/dist/react-datepicker.css';
import { AppointmentSchedulerStyles } from './AppointmentSchedulerStyles';
import { updateSessionData } from '@/helpers';
import { useRouter } from 'next/router';
import getLeadValues from '@/services/getLeadValues';
import { timeMap } from '@/services';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

const AppointmentSchedulerComponent = ({
  handleNextStep,
  IsStimulus = false,
  mode = 'hero',
  PaidCampaign,
  stage,
  subtitle,
  title,
  campaign,
  submitting,
  EntityCampaign = false,
  appointmentText = 'Appointment',
  confirmText = 'Confirm',
  setCurrentStep = null,
  setIsLoading = false,
  NumDaysOverride = null,
  EForm = false,
}) => {
  const [schedulerIsLoading, setSchedulerIsLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [disableConfirm, setDisableConfirm] = useState(false);
  const [allSlots, setAllSlots] = useState([]); // All datetime slots retrieved from the backend API
  const [loading, setLoading] = useState(false);
  const [firstDateSlots, setFirstDateSlots] = useState(false); // Determines if the date slots, beginning with the first have been fulfilled
  const [firstDateSlotsAvail, setFirstDateSlotsAvail] = useState(true); // Determines if the slots from the first date has 0 available
  const [dateSlots, setDateSlots] = useState(false);
  const [dateSlotsFilled, setDateSlotsFilled] = useState(false);
  const [currentTime, setCurrentTime] = useState(null); //Time to be shown on the scheduler
  const [schedulerSelectedDate, setSchedulerSelectedDate] = useState(null); // The selected Date on the scheduler { month,  monthName, date, day dayName, year }
  const [schedulerSelectedTimes, setSchedulerSelectedTimes] = useState([]); //Timeslots for the selected date on the scheduler
  const [firstAvailableTimeSlot, setFirstAvailableTimeSlot] = useState(null); // The first available timeslot for mobile scroll
  const [skipSaturday, setSkipSaturday] = useState(false);
  const [toggleScheduler, setToggleScheduler] = useState(false);
  const [availableDates, setAvailableDates] = useState([]) //The dates the be shown as available for selection on the calendar  { month,  monthName, date, day dayName, year }
  const [availableTimes, setAvailableTimes] = useState([
    { hours: 9, minutes: 30, available: false },
    { hours: 10, minutes: 0, available: false },
    { hours: 10, minutes: 30, available: false },
    { hours: 11, minutes: 0, available: false },
    { hours: 11, minutes: 30, available: false },
    { hours: 12, minutes: 0, available: false },
    { hours: 12, minutes: 30, available: false },
    { hours: 13, minutes: 0, available: false },
    { hours: 13, minutes: 30, available: false },
    { hours: 14, minutes: 0, available: false },
    { hours: 14, minutes: 30, available: false },
    { hours: 15, minutes: 0, available: false },
    { hours: 15, minutes: 30, available: false },
    { hours: 16, minutes: 0, available: false },
    { hours: 16, minutes: 30, available: false },
    { hours: 17, minutes: 0, available: false },
    { hours: 17, minutes: 30, available: false },
    { hours: 18, minutes: 0, available: false },
    { hours: 18, minutes: 30, available: false },
    { hours: 19, minutes: 0, available: false },
    { hours: 19, minutes: 30, available: false },
    { hours: 20, minutes: 0, available: false },
    { hours: 20, minutes: 30, available: false },
    { hours: 21, minutes: 0, available: false },
    { hours: 21, minutes: 30, available: false },
  ])
  const router = useRouter();

  // This sets the first timeslot of the calendar to the users current time
  // Set timeslots to display proper time, no matter where the users timezone is
  // 8am est 
  useEffect(() => {
    const dt = new Date();

    // Check if daylight savings time
    let isDST = false;
    const currentEasternTime = new Date().toLocaleString("en-US", { timeZone: "America/New_York", timeZoneName: "long" });
    if (currentEasternTime.includes("Daylight")) {
      isDST = true;
    }

    if (isDST) {
      dt.setUTCHours(12);
    } else {
      dt.setUTCHours(13);
    }

    //Loop through the times, and adjust the times according to local time compared to UTC
    for (let i = 0; i <= availableTimes.length - 1; i++) {
      let currentElement = availableTimes[i];
      if (currentElement.minutes === 30) {
        currentElement.hours = dt.getHours();
        dt.setHours(dt.getHours() + 1);
        continue;
      }
      currentElement.hours = dt.getHours();
    }
  }, []);

  //Set current time for a user
  useEffect(() => {
    const formatTimezone = () => dayjs(new Date()).format('z (h:mm A)');
    setCurrentTime(formatTimezone());
  }, [])

  let setUpAvailableDates = []

  //Sets available days for scheduling
  useEffect(() => {
    // List of Months / Days of the week
    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const daysOfTheWeek = ["Sun", "Mon", "Tues", "Wed", "Thurs", "Fri", "Sat"];

    //Current day of user
    const currentDate = new Date();

    //Add new starting date to calendar choices if NumDaysOverride is available
    if (NumDaysOverride) {
      currentDate.setDate(currentDate.getDate() + NumDaysOverride);
    }

    //If there are no slots available for the first date, reset and go to the next date
    if (!firstDateSlotsAvail) {
      // Reset the available dates and add one
      setUpAvailableDates = [];
      currentDate.setDate(currentDate.getDate() + 1);
    }

    if (skipSaturday) {
      setUpAvailableDates = [];
      setDateSlots(false);
      setDateSlotsFilled(false);
    }

    // Used to set and check for Eastern Time
    const dt = new Date();
    // dt in utc time to translate to Eastern Time
    dt.setTime(dt.getTime() + dt.getTimezoneOffset() * 60 * 1000);

    // Determine if we have already skipped one day for past 8PM
    // Will cause infinite loop without this check with the hours conditional
    let nextDayHours = false;

    // Check if daylight savings time
    let isDST = false;
    const currentEasternTime = new Date().toLocaleString("en-US", { timeZone: "America/New_York", timeZoneName: "long" });
    if (currentEasternTime.includes("Daylight")) {
      isDST = true;
    }

    // current timezone offset
    // required to handle timezones outside of us when to skip the next day
    const currentTimezoneOffset = new Date().getTimezoneOffset();
    let offset;
    if (isDST) {
      offset = 4;
    } else {
      offset = 5;
    }

    // Set the minimum hours from Eastern time, when to go to next day.
    // If time is between 9PM - 12AM ET, show next day as first available date
    // If time is after 12AM ET show the current day as the first available date
    let minHours = currentTimezoneOffset / 60 - offset;

    // //Get dates from today for next 3 days (excluding holidays/weekends) or if time is past 8pm
    // while (setUpAvailableDates?.length !== 3) {
    // Temporary 4 days avail
    while (setUpAvailableDates?.length !== 4) {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth(); // 0 indexed, 0 = January
      const date = currentDate.getDate();
      const day = currentDate.getDay(); // 0 indexed, 0 = Sunday, 6 = Saturday
      // Eastern time hours
      let hours = parseInt(new Date().toLocaleString("en-US", { timeZone: "America/New_York", hour: 'numeric', hour12: false })); // 1 - 24 for midnight

      // If it is midnight, set the hours to 0
      // Handles the calculations easier
      if (hours === 24) {
        hours = 0;
      }

      // check to see if it is a weekend && 8pm or later in EST
      // also ensure that minimum hours for current user is past midnight for Eastern time
      // ex.
      // minHours = -1 = maxHours 23 (11PM local);
      let maxHours;
      // when minHours is negative it causes an issue in the calculation
      if (minHours < 0) {
        maxHours = 24 + minHours;
      }

      // For timezones outside of US
      // Ensure that past midnight EST shows the current day availability
      // Between 8PM ET and Midnight shows the next day availability
      if (maxHours) {
        if (maxHours > hours && hours > 19 && !nextDayHours || maxHours < hours && !nextDayHours) {
          currentDate.setDate(currentDate.getDate() + 1);
          nextDayHours = true;
          continue;
        }
      }

      // For timezones within the US 
      if (hours > 19 && !nextDayHours && !maxHours || hours < minHours && !nextDayHours) {
        currentDate.setDate(currentDate.getDate() + 1);
        nextDayHours = true;
        continue;
      }

      // If Sunday, go to the next day.
      if (day === 0) {
        currentDate.setDate(currentDate.getDate() + 1);
        continue;
      }

      // Skip saturday if the return value from checking saturday availability is no available slots
      if (day === 6 && skipSaturday) {
        currentDate.setDate(currentDate.getDate() + 1);
        continue;
      }

      // Holidays
      if (month === 6 && date === 4) {
        currentDate.setDate(currentDate.getDate() + 1);
        continue;
      }
      if (date === 24 && month === 11) {
        currentDate.setDate(currentDate.getDate() + 1);
        continue;
      }
      if (date === 1 && month === 0) {
        currentDate.setDate(currentDate.getDate() + 1);
        continue;
      }
      if (date === 28 && month === 5) {
        currentDate.setDate(currentDate.getDate() + 1);
        continue;
      }
      if (date === 4 && month === 6) {
        currentDate.setDate(currentDate.getDate() + 1);
        continue;
      }
      if (date === 5 && month === 8) {
        currentDate.setDate(currentDate.getDate() + 1);
        continue;
      }

      if (date === 24 && month === 10) {
        currentDate.setDate(currentDate.getDate() + 1);
        continue;
      }
      if (date === 26 && month === 11) {
        currentDate.setDate(currentDate.getDate() + 1);
        continue;
      }
      if (date === 31 && month === 11) {
        currentDate.setDate(currentDate.getDate() + 1);
        continue;
      }

      if (date === 2 && month === 0) {
        currentDate.setDate(currentDate.getDate() + 1);
        continue;
      }

      if (date === 29 && month === 4) {
        currentDate.setDate(currentDate.getDate() + 1);
        continue;
      }

      setUpAvailableDates.push({ month: month, monthName: monthNames[month], date: date, day: day, dayName: daysOfTheWeek[day], year: year });
      //Add one to the current day
      currentDate.setDate(currentDate.getDate() + 1);
      continue;
    }
    //Set the set up dates as the available dates
    setAvailableDates(setUpAvailableDates);
    //Set the inital first day as the selected day;
    setSchedulerSelectedDate(setUpAvailableDates[0])

  }, [firstDateSlotsAvail, skipSaturday]);

  //Get timeslots to be displayed
  useEffect(() => {
    setUpAvailableDates = [];
    // setSchedulerIsLoading(true);
    //retrieve time slots for days that are selected on the calendar
    const currentTimeslots = allSlots.filter((slot) => {
      const thisAppt = new Date(0);
      thisAppt.setUTCMilliseconds(slot);

      const apptMonth = thisAppt.getMonth();
      const apptDate = thisAppt.getDate();

      const now = new Date();

      // filter out past dates and appointments available less than 3 minutes from now
      if (thisAppt < dayjs(now).add(3, 'minute')) return null;

      //Return slots that are from the selected date and month only
      if (schedulerSelectedDate?.month === apptMonth && schedulerSelectedDate?.date === apptDate) {
        return slot;
      }
      return null;
    })

    //converts the selected dates time slots into hours and minutes to compare to total slots
    const mappedTimeSlots = currentTimeslots.map((slot) => {
      const thisAppt = new Date(0);
      thisAppt.setUTCMilliseconds(slot);

      return { hours: thisAppt.getHours(), minutes: thisAppt.getMinutes(), value: slot }
    })

    const timeSlotsMap = {}; // {slot hour "h" slot minutes : slot value} {5h30 : 1657152000000, 6h0 : 1657252000000, 13h30 : 1657382000000}

    // Iterate over mapped Time slots and fill up the availabilty timeSlotsMap
    mappedTimeSlots.forEach(slot => {
      timeSlotsMap[`${slot.hours}h${slot.minutes}`] = slot.value
    })

    const displayTimeSlots = availableTimes.map((element) => {
      let isAvailable = false;
      let value;

      // If the element hours and minutes seperated by "h" exist
      // return that time slot with the value as available
      if (timeSlotsMap[`${element.hours}h${element.minutes}`]) {
        isAvailable = true;
        value = timeSlotsMap[`${element.hours}h${element.minutes}`];
      }
      return { hours: element.hours, minutes: element.minutes, available: isAvailable, value: value };
    })

    setSchedulerSelectedTimes(displayTimeSlots);

    if (firstDateSlots && dateSlots) {
      //Set the status of all slots filled to be true, once the first date slots and second date slots have been filled
      setDateSlotsFilled(true);
    }
  }, [schedulerSelectedDate, availableDates, firstDateSlots, allSlots]);


  //Retrieve the first available date for scroll on mobile
  useEffect(() => {
    if (schedulerSelectedTimes) {
      for (let i = 0; i < schedulerSelectedTimes.length; i++) {
        let currentElement = schedulerSelectedTimes[i];
        if (currentElement.available) {
          setFirstAvailableTimeSlot(i);
          break;
        }
      }
    }
  }, [schedulerSelectedDate, schedulerSelectedTimes]);

  // Handles scrolling into view of earliest time slot on Mobile
  useEffect(() => {
    let timeContainer;
    let firstTimeSlot;
    let timesContainer;
    let docWidth;
    //Ensure the document is loaded before performing any selections
    if (document) {
      firstTimeSlot = document.getElementById(`time-slot-${firstAvailableTimeSlot}`);
      timesContainer = document.getElementsByClassName('time-container');
      docWidth = document.body.clientWidth;

      // Scroll only if the screen width is less than 500px;
      if (docWidth < 500) {
        // Retrieve the correct element by class name
        if (timesContainer?.length) {
          timeContainer = timesContainer[0];
        }
        //scroll the first time slot into view of the nearest offset parent time-container
        if (firstTimeSlot && timeContainer) {
          let topPos = firstTimeSlot.offsetTop;
          timeContainer.scrollTo({ top: topPos, behavior: 'smooth' })
        }
      }
    }
  }, [firstAvailableTimeSlot])


  //Check for Saturday availability, and skip to next day if Saturday is not available.
  useEffect(() => {
    const fn = async (dateOverride, numberDaysOverride) => {
      let leadID;
      if (setCurrentStep) {
        leadID = Cookies.get('ContactID');
      } else {
        leadID = Cookies.get('LeadID');
      }

      let appointmentType = '1on1';
      if (setCurrentStep) {
        appointmentType = 'Discovery'
      } else if (EForm) {
        appointmentType = 'EForm'
      }

      let url = `/api/backend/nwapi/slots?appointmentType=${appointmentType}${leadID ? `&WID=${leadID}` : ''
        }&PaidCampaign=${PaidCampaign}${EntityCampaign ? '&EntityCampaign=1' : ''}`;
      if (IsStimulus) url += '&IsStimulus=1';
      if (campaign) url += `&Campaign=${campaign}`;
      // if (EntityCampaign) {
      //   url += `&EntityCampaign=${EntityCampaign}`;
      // }
      if (NumDaysOverride || numberDaysOverride) url += `&NumDaysOverride=${numberDaysOverride || NumDaysOverride}`;
      if (dateOverride) url += `&DateOverride=${dateOverride}`

      const leadValues = getLeadValues();
      if (leadValues && leadValues.team) {
        url += '&Team=' + leadValues.team;
      }

      const response = await axios.get(url);
      const { slots } = response.data;

      if (!slots?.length) {
        setSkipSaturday(true);
      }
    }

    if (availableDates?.length === 4 && !skipSaturday) {
      // if (availableDates[1].dayName === 'Sat' || availableDates[2].dayName === 'Sat') {
      if (availableDates[1].dayName === 'Sat' || availableDates[2].dayName === 'Sat' ||
        availableDates[3].dayName === 'Sat') {

        let saturdayIndex;
        availableDates.forEach((ele, i) => {
          if (ele.dayName === 'Sat') {
            saturdayIndex = i
          }
        })

        if (saturdayIndex) {
          let firstDate = availableDates[saturdayIndex];
          let firstDateYear = firstDate?.year;
          let firstDateMonth = firstDate?.month; // month is 0 indexed
          let firstDateDate = firstDate?.date;
          let dateOverride;

          // retrieve the Saturday day only
          let numberDaysOverride = 1;

          //Properly format the date for backend API
          firstDateMonth = firstDateMonth + 1 //increase to account for 0 index

          if (String(firstDateMonth)?.length === 1) {
            firstDateMonth = `0${firstDateMonth}`
          }

          if (String(firstDateDate)?.length === 1) {
            firstDateDate = `0${firstDateDate}`
          }
          if (firstDateYear && firstDateMonth && firstDateDate) {
            // dateOverride format = YYYY-MM-DD
            dateOverride = `${firstDateYear}-${firstDateMonth}-${firstDateDate}`;
          }

          fn(dateOverride, numberDaysOverride);
        }

      }
    }
  }, [availableDates]);

  //Retrieves time slots for First day
  useEffect(() => {

    // These two function calls must kept inside of the useEffect to properly utilize the props??
    //i.e. setCurrentStep, PaidCampaign, EntityCampaign
    //Reach out to someone about this to restructure
    const fn = async (dateOverride, numberDaysOverride) => {
      // setLoading(true);
      // A conditional to use ContactID for leadID if coming from TSF.
      let leadID;
      if (setCurrentStep) {
        leadID = Cookies.get('ContactID');
      } else {
        leadID = Cookies.get('LeadID');
      }

      let appointmentType = '1on1';
      if (setCurrentStep) {
        appointmentType = 'Discovery'
      } else if (EForm) {
        appointmentType = 'EForm'
      }

      let url = `/api/backend/nwapi/slots?appointmentType=${appointmentType}${leadID ? `&WID=${leadID}` : ''
        }&PaidCampaign=${PaidCampaign}${EntityCampaign ? '&EntityCampaign=1' : ''}`;
      if (IsStimulus) url += '&IsStimulus=1';
      if (campaign) url += `&Campaign=${campaign}`;
      // if (EntityCampaign) {
      //   url += `&EntityCampaign=${EntityCampaign}`;
      // }
      if (NumDaysOverride || numberDaysOverride) url += `&NumDaysOverride=${numberDaysOverride || NumDaysOverride}`;
      if (dateOverride) url += `&DateOverride=${dateOverride}`

      const leadValues = getLeadValues();
      if (leadValues && leadValues.team) {
        url += '&Team=' + leadValues.team;
      }

      //Toggle the loading screen before the axios query
      if (router.query.slug && router.query.slug == 'accounting-services-geo-2') {
        setLoading(true)
      }
      const response = await axios.get(url);
      const { assignedAccountantID, slots } = response.data;

      // save assignedAccountantID to session
      if (assignedAccountantID != undefined) {
        sessionStorage.setItem('assignedAccountantID', assignedAccountantID);
      }

      if (slots?.length) {
        let prevSlots = [...allSlots];
        const modifiedSlots = slots.map(slot => {
          const d = new Date(0);
          return d.setUTCSeconds(slot);
        });
        await setAllSlots([...prevSlots, ...modifiedSlots]);

        //sets that the first date slots have been loaded
        setFirstDateSlots(true);
        //stops the scheduler from loading
        setSchedulerIsLoading(false);
      } else {
        // Will trigger the useEffect the add an additional day for no slots
        setFirstDateSlotsAvail(false);
      }

      setTimeout(function () { setLoading(false); }, 1000)
      // handles the scheduler to stop loading

      if (setIsLoading) {
        setIsLoading(false);
      }
    };

    if (availableDates?.length && !firstDateSlots) {
      let firstDate = availableDates[0];
      let firstDateYear = firstDate?.year;
      let firstDateMonth = firstDate?.month; // month is 0 indexed
      let firstDateDate = firstDate?.date;
      let dateOverride;

      // retrieve the first day only
      let numberDaysOverride = 1;

      //Properly format the date for backend API
      firstDateMonth = firstDateMonth + 1 //increase to account for 0 index

      if (String(firstDateMonth)?.length === 1) {
        firstDateMonth = `0${firstDateMonth}`
      }

      if (String(firstDateDate)?.length === 1) {
        firstDateDate = `0${firstDateDate}`
      }
      if (firstDateYear && firstDateMonth && firstDateDate) {
        // dateOverride format = YYYY-MM-DD
        dateOverride = `${firstDateYear}-${firstDateMonth}-${firstDateDate}`;
      }

      fn(dateOverride, numberDaysOverride);
    }

  }, [availableDates]);

  // Retrieve time slots for the rest of the days
  useEffect(() => {
    // These two function calls must kept inside of the useEffect to properly utilize the props??
    // i.e. setCurrentStep, PaidCampaign, EntityCampaign
    //Reach out to someone about this to restructure
    const fn = async (dateOverride, numberDaysOverride) => {
      setToggleScheduler(true);
      // A conditional to use ContactID for leadID if coming from TSF.
      let leadID;
      if (setCurrentStep) {
        leadID = Cookies.get('ContactID');
      } else {
        leadID = Cookies.get('LeadID');
      }

      let appointmentType = '1on1';
      if (setCurrentStep) {
        appointmentType = 'Discovery'
      } else if (EForm) {
        appointmentType = 'EForm'
      }

      let url = `/api/backend/nwapi/slots?appointmentType=${appointmentType}${leadID ? `&WID=${leadID}` : ''
        }&PaidCampaign=${PaidCampaign}${EntityCampaign ? '&EntityCampaign=1' : ''}`;
      if (IsStimulus) url += '&IsStimulus=1';
      if (campaign) url += `&Campaign=${campaign}`;
      // if (EntityCampaign) {
      //   url += `&EntityCampaign=${EntityCampaign}`;
      // }
      if (NumDaysOverride || numberDaysOverride) url += `&NumDaysOverride=${numberDaysOverride || NumDaysOverride}`;
      if (dateOverride) url += `&DateOverride=${dateOverride}`

      const leadValues = getLeadValues();
      if (leadValues && leadValues.team) {
        url += '&Team=' + leadValues.team;
      }

      //Toggle the loading screen before the axios query
      if (router.query.slug && router.query.slug == 'accounting-services-geo-2') {
        setLoading(true)
      }
      const response = await axios.get(url);
      const { assignedAccountantID, slots } = response.data;

      // save assignedAccountantID to session
      if (assignedAccountantID != undefined) {
        sessionStorage.setItem('assignedAccountantID', assignedAccountantID);
      }
      if (slots) {
        let prevSlots = [...allSlots];
        const modifiedSlots = slots.map(slot => {
          const d = new Date(0);
          return d.setUTCSeconds(slot);
        });
        await setAllSlots([...prevSlots, ...modifiedSlots]);
      }
      setTimeout(function () { setLoading(false); }, 1000)
      //Handles the scheduler to stop loading
      setSchedulerIsLoading(false);

      if (setIsLoading) {
        setIsLoading(false);
      }

      setToggleScheduler(false);
    };

    // Temp 7 days, set this to the number of days shown
    // return to === 3
    if (availableDates?.length === 4 && allSlots?.length && !dateSlots) {
      // Second date values
      let secondDateObj = availableDates[1];
      let secondDateYear = secondDateObj?.year;
      let secondDateMonth = secondDateObj?.month; // month is 0 indexed
      let secondDateDate = secondDateObj?.date;

      // Third date values
      let thirdDateObj = availableDates[2];
      let thirdDateYear = thirdDateObj?.year;
      let thirdDateMonth = thirdDateObj?.month; // month is 0 indexed
      let thirdDateDate = thirdDateObj?.date;

      //last date values

      let lastDateObj = availableDates[availableDates.length - 1];
      let lastDateYear = lastDateObj?.year;
      let lastDateMonth = lastDateObj?.month; // month is 0 indexed
      let lastDateDate = lastDateObj?.date;

      //Calculate the number of days between the second and third available day
      let oneDay = 24 * 60 * 60 * 1000; // hours*minutes*seconds*milliseconds
      let secondDateCalc = new Date(secondDateYear, secondDateMonth, secondDateDate, 12, 0, 0, 0); // second date year, month, date, set to noon
      let thirdDateCalc = new Date(thirdDateYear, thirdDateMonth, thirdDateDate, 12, 0, 0, 0); // third date year, month, date, set to noon
      // Temporary last date calc
      let lastDateCalc = new Date(lastDateYear, lastDateMonth, lastDateDate, 12, 0, 0, 0); // third date year, month, date, set to noon

      //Number of days between second and third day
      let diffDaysTotal = Math.round(Math.abs((secondDateCalc.getTime() - lastDateCalc.getTime()) / (oneDay)));

      let dateOverride;
      let numberDaysOverride;

      secondDateMonth = secondDateMonth + 1 // Properly format the second month

      //Properly format the date for backend API
      if (String(secondDateMonth)?.length === 1) {
        secondDateMonth = `0${secondDateMonth}`
      }

      if (String(secondDateDate)?.length === 1) {
        secondDateDate = `0${secondDateDate}`
      }
      if (secondDateYear && secondDateMonth && secondDateDate) {
        // dateOverride format = YYYY-MM-DD
        dateOverride = `${secondDateYear}-${secondDateMonth}-${secondDateDate}`;
      }

      // Number of days 1 (current) including days between this and current
      numberDaysOverride = 1 + diffDaysTotal;

      //Load the rest of the days
      fn(dateOverride, numberDaysOverride);
      setDateSlots(true);
    }
  }, [allSlots, skipSaturday, availableDates])

  useEffect(() => {
    //Ensure that the dates and selectedDate are available
    if (schedulerSelectedDate && availableDates) {
      const selectDate = schedulerSelectedDate.date;
      const selectMonth = schedulerSelectedDate.month;
      // If the selected Date is the second or third date
      // Fire the loading screen if we select the second date and month
      if (selectDate === availableDates[1]?.date && selectMonth === availableDates[1]?.month || selectDate === availableDates[2]?.date && selectMonth === availableDates[2]?.month) {
        // Show the loading screen animation if all slots have not been filled.
        if (!dateSlotsFilled) {
          setSchedulerIsLoading(true);
          //Will no longer show loading animation if slots are all filled
          setDateSlotsFilled(true);
        } else {
          setSchedulerIsLoading(false);
        }
      }
    }
  }, [schedulerSelectedDate])

  //When both date and time have been updated, pass the data to the schedule appointment function from UniversalSchedulerV2
  useEffect(() => {
    if (selectedDate && selectedTime) {
      handleConfirm();
    }
  }, [selectedDate, selectedTime])

  const confirm = async () => {
    setSchedulerIsLoading(true);
    // If coming from TSF saeve the selected date and time
    const TSFData = sessionStorage.getItem('tax_forecast_data');
    if (TSFData) {
      updateSessionData('tax_forecast_data', { 'selectedDate': selectedDate, 'selectedTime': selectedTime });
    }
    setDisableConfirm(true);
    let promise = handleNextStep({ selectedDate, selectedTime });
    if (promise && typeof promise.then === 'function' && promise[Symbol.toStringTag] === 'Promise') {
      await promise;
    }
    setSchedulerIsLoading(false);
    setDisableConfirm(false);
  };

  const handleConfirm = async () => {
    confirm();
  };

  //Sets the date selected on the calendar to display timeslots
  const handleSchedulerDateSelection = (slot) => {
    // Set the selected date as selected
    setSchedulerSelectedDate(slot);
    //Handles the loader while 2nd and 3rd date slots are loading
    if (slot === availableDates[1] || slot === availableDates[2] && toggleScheduler) {
      setSchedulerIsLoading(true);
    }
  }

  //Converts the slot data to be used to schedule an appointment
  const handleDateAndTimeConversion = (slot) => {
    const thisAppt = new Date(0);
    thisAppt.setUTCMilliseconds(slot.value);

    setSelectedDate(thisAppt);
    setSelectedTime(slot.value);
  }

  return (
    <div className="container">
      <div className="row">
        <div className="col-md-12">
          <AppointmentSchedulerStyles
            className={[
              schedulerIsLoading || submitting ? 'is-loading ppointment-scheduler' : 'ppointment-scheduler',
            ].join(' ')}
            mode={mode}
            submitting={submitting}
          //className="appointment-scheduler"
          >
            {(title || subtitle) && (
              <div className="row">
                <div className="col-md-12">
                  <h3 className="calendar-title">{title}</h3>
                  <p className="calendar-subtitle">{subtitle}</p>
                </div>
              </div>
            )}
            {mode === 'standalone' && (
              <div className="accountant-details">
                <Image
                  src="/static/images/Susan_Young_color.png"
                  alt="accountant avatar"
                  className="accountant-avatar"
                  width={80}
                  height={80}
                />
              </div>
            )}
            <div className="appointment-container">
              {schedulerSelectedDate && (
                <div className="scheduler-selected-date">
                  {schedulerSelectedDate.monthName} {schedulerSelectedDate.year}
                </div>
              )}
              <div className="sub-title-desktop">Select a time and we’ll give you a call</div>
              <div className="date-component-container">
                <div className="date-selector-container">
                  <div className="date-selector-wrapper">
                    {schedulerSelectedDate && availableDates.map((slot, i) =>
                      <div key={i} className={schedulerSelectedDate.month === slot.month && schedulerSelectedDate.date === slot.date ? "date-selector-border active-border" : "date-selector-border"}>
                        <div
                          className={schedulerSelectedDate.month === slot.month && schedulerSelectedDate.date === slot.date ? "date-selector active" : "date-selector"}
                          onClick={() => handleSchedulerDateSelection(slot)}
                          key={i}
                        >
                          <div className="date">
                            {slot.date}
                          </div>
                          <div className="day-name">
                            {slot.dayName}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="current-time-container">
                  {currentTime && (
                    <div className="current-time">
                      {`Time displayed in ${currentTime}`}
                    </div>)}
                </div>
                <div className="time-container">
                  {schedulerSelectedTimes.map((slot, i) =>
                    <div
                      className={`time-slot ${slot.available ? 'active' : 'disabled'}`}
                      onClick={() => handleDateAndTimeConversion(slot)}
                      key={i}
                      id={`time-slot-${i}`}
                    >
                      <div>
                        {/* If the slot includes minutes, add it to the key to check in time map */}
                        {/* timeMap contains all avaiable appointment slots, which hours are adjusted for user's time in a useEffect above  */}
                        {slot.minutes === 30 ? timeMap[String(slot.hours) + String(slot.minutes)] : timeMap[String(slot.hours)]}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="current-time-container desktop">
                {currentTime && (
                  <div className="current-time">
                    {`Time displayed in ${currentTime}`}
                  </div>)}
              </div>
            </div>
            {loading &&
              <div className='calendar-loader-wrapper'>
                <div className="calendar-loader">
                  <Image
                    src="/static/images/loading-v2.gif"
                    width={384}
                    height={216}
                    objectFit="contain"
                  />
                </div>
                <p className="calendar-loader-text">
                  Accounting for your network speed.
                </p>
              </div>
            }
          </AppointmentSchedulerStyles>
        </div>
      </div>
    </div>
  );
};

export default AppointmentSchedulerComponent;
